import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import FanVerseLogo from "@/components/FanVerseLogo";
import { Twitter, Github, Mail, Loader2 } from "lucide-react";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      toast.success(res.data.message);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    }
    setSubscribing(false);
  };

  return (
    <footer className="bg-[#0B0F19] border-t border-white/5 py-12 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Newsletter */}
        <div className="mb-10 pb-10 border-b border-white/5">
          <div className="max-w-xl mx-auto text-center">
            <Mail className="w-8 h-8 text-amber-500 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Stay in the Loop</h3>
            <p className="text-slate-400 text-sm mb-4">Get the latest lore, new universes, and community highlights delivered to your inbox.</p>
            <form onSubmit={handleSubscribe} className="flex gap-2 max-w-md mx-auto" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-500/30"
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="btn-primary px-5 py-2.5 text-sm flex items-center gap-1.5"
                data-testid="newsletter-submit"
              >
                {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {subscribing ? "" : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <FanVerseLogo size={40} />
            </Link>
            <p className="text-slate-400 text-sm max-w-md">
              Your ultimate destination for exploring fictional fandom universes. 
              From Star Trek to Marvel, dive deep into the lore, characters, and worlds you love.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-4">Explore</h4>
            <ul className="space-y-2">
              <li><Link to="/?category=sci-fi" className="text-sm text-slate-400 hover:text-white transition-colors">Sci-Fi Universes</Link></li>
              <li><Link to="/?category=fantasy" className="text-sm text-slate-400 hover:text-white transition-colors">Fantasy Realms</Link></li>
              <li><Link to="/?category=superhero" className="text-sm text-slate-400 hover:text-white transition-colors">Superhero Worlds</Link></li>
              <li><Link to="/?category=horror" className="text-sm text-slate-400 hover:text-white transition-colors">Horror Dimensions</Link></li>
            </ul>
          </div>

          {/* Account */}
          <div>
            <h4 className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-4">Account</h4>
            <ul className="space-y-2">
              <li><Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors">Sign In</Link></li>
              <li><Link to="/register" className="text-sm text-slate-400 hover:text-white transition-colors">Create Account</Link></li>
              <li><Link to="/favorites" className="text-sm text-slate-400 hover:text-white transition-colors">My Favorites</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} FanVerse. All fictional universes belong to their respective owners.
          </p>
          <div className="flex items-center gap-4">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors">
              <Github className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
